import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation  } from 'react-router-dom';

import { CircularProgress, Snackbar, Alert, Button, IconButton, Box } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';

import useErrorManager from "../../hooks/useErrorManager";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

import PageContainer from '../Layout/PageContainer';
import ConfirmationDialog from '../Layout/ConfirmationDialog';
import GenericTable from '../Layout/Table/GenericTable';
import TextFilter from "../Layout/Table/TextFilter"
import TextLinkButton from "../Layout/TextLinkButton"

import { useNotification } from '../../contexts/NotificationContext';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext'; // Import breadcrumb context


function FwdAssignments({ fetchRoute = '/fwd/assg/list', disableBreadcrumb = false }) {
    const [fwdAssg, setFwdAssg] = useState([]);
    const [filterText, setFilterText] = useState("");

    const [loading, setLoading] = useState(true);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [selectedRowId, setSelectedRowId] = useState(null);
    
    const { setError, clearError, getError } = useErrorManager();
    const { showNotification } = useNotification();
    const { updateBreadcrumb } = useBreadcrumb(); // Use breadcrumb context to update breadcrumb

    const navigate = useNavigate();
    const axiosInstance = useAxiosPrivate();

    const columns = [
        { id: 'delete', label: '', render: (item) => 
            <IconButton
                onClick={() => handleDialogOpen(item.id)}
                color="error"
            >
                <Delete />
            </IconButton>
        },
        // { id: 'delete', label:"", render: (item) =>  HandleFwdAssgnDelete(item.id) },
        { id: 'name', label: 'Location/Gateway', filtered: true, render: (item) =>  renderFwdAssgName(item)},
        { id: 'objType', label: 'Assigned to', render: (item) => renderFwdAssgObjType(item) },
        { id: 'profileName', label: 'Forwarding profile', filtered: true, render: (item) => <TextLinkButton action={() =>navigate(`/profiles/${item.profileId}`)} text={item.profileName}/> },
        { id: 'refProfileReportType', label: 'Action type', render: (item) => renderFwdProfileType(item.refProfileReportType) },
        { id: 'profileType', label: 'Profile type', render: (item) => item.profile.profileType  },
        { id: 'profileDestinationCount', label: 'Number of destinations' },
        { id: 'profileAssignmentCount', label: 'Number of forwarding assignments' }
    ];

    useEffect(() => {

        if(!disableBreadcrumb){
            updateBreadcrumb([
                { label: 'Manage Forwarding Assignments'}
            ]);
        }

        const fetchFwdAssignments = async () => {
            try {
                const response = await axiosInstance.get(fetchRoute, {
                    withCredentials: true,
                });
                setFwdAssg(response.data);
                clearError('fetch');
                setLoading(false);
                
            } catch (error) {
                setError('fetch',error.message);
                showNotification('Failed to fetch Forwarding Assignments','error')
                setLoading(false);
            }
        };

        fetchFwdAssignments();
    }, []);

    const handleDialogOpen = (assgId) => {
        setSelectedRowId(assgId); // Store the ID of the row to delete
        setOpenConfirmDialog(true); // Open the confirmation dialog
    };

    const handleDialogClose = () => {
        setSelectedRowId(null); // Clear the selected ID
        setOpenConfirmDialog(false); // Close the dialog
    };


    const HandleFwdAssgnDelete = async () => {
        try {
            await axiosInstance.delete(`/fwd/assg/${selectedRowId}`);
            clearError('delete');
            setFwdAssg((prev) => prev.filter((item) => item.id !== selectedRowId)); // Update the table data
            showNotification('Row deleted successfully', 'success');
        } catch (error) {
            setError('delete', error);
            showNotification('Failed to delete location','error')
        } finally {
            handleDialogClose();
        }
    };



    // const renderFwdAssgName = (item) => {
    //     const priorityFields = [
    //         { idKey: 'locationGroupId', nameKey: 'locationGroupName' },
    //         { idKey: 'locationId', nameKey: 'locationName' },
    //         { idKey: 'gatewayId', nameKey: 'gatewayName' }
    //     ];
    
    //     const buttonVal = priorityFields.find(({ idKey }) => item[idKey]) || {};
    
    //     return (
    //         <TextLinkButton
    //             action={() => console.log(item[buttonVal.idKey])}
    //             text={item[buttonVal.nameKey]}
    //         />
    //     );
    // };
    const renderFwdAssgName = (item) => {
        const priorityFields = [
            { idKey: 'locationGroupId', nameKey: 'locationGroupName', route: 'locations' }, // Navigate to location list for location group
            { idKey: 'locationId', nameKey: 'locationName', route: 'locations' },
            { idKey: 'gatewayId', nameKey: 'gatewayName', route: 'gateways' }
        ];
    
        // Find the first matching field
        const matchedField = priorityFields.find(({ idKey }) => item[idKey]) || {};
    
        // Handle special case for location groups
        const navigateTo = matchedField.idKey === 'locationGroupId'
            ? `/${matchedField.route}` // Navigate to the location list page
            : `/${matchedField.route}/${item[matchedField.idKey]}`; // Default navigation
    
        // Render the button
        return matchedField.idKey ? (
            <TextLinkButton
                action={() => navigate(navigateTo)}
                text={item[matchedField.nameKey]}
            />
        ) : null; // Return null if no matching field is found
    };
    

    const renderFwdAssgObjType = (item) => {
        const priorityFields = [
            { idKey: 'locationGroupId', nameKey: 'Location group' },
            { idKey: 'locationId', nameKey: 'Location' },
            { idKey: 'gatewayId', nameKey: 'Gateway' }
        ];
    
        const buttonVal = priorityFields.find(({ idKey }) => item[idKey]) || {};
    
        return buttonVal.nameKey;
    };

    const renderFwdProfileType = (dataType) => {
        switch (dataType) {
            case 'QnodeReadout':
                return 'Network readout';
                break;
            
            case 'DMReadout':
                return 'Meter readout';
                break;

            default:
                return dataType;
                break;
        }
    };


    const handleFilterChange = (event) => {
        setFilterText(event.target.value);
    };

    return (
            <PageContainer>
                {loading ? (
                    <CircularProgress />
                ) : (
                    <>
                        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="10px">
                            <TextFilter
                                columns={columns}
                                onFilterChange={handleFilterChange}
                            />
                            <Button onClick={() => navigate(`/assignments/create`)} variant="contained" color="primary">
                                <Add/>
                            </Button>
                        </Box>
                        <GenericTable
                            items={fwdAssg}
                            columns={columns}
                            filterText={filterText}
                        />
                        <ConfirmationDialog
                            open={openConfirmDialog}
                            onClose={handleDialogClose}
                            onConfirm={HandleFwdAssgnDelete}
                            title="Confirm Deletion"
                            description="Are you sure you want to delete this assignment? This action cannot be undone."
                            confirmText="Delete"
                            cancelText="Cancel"
                        />
                    </>
                )}
            </PageContainer>
    );
}

export default FwdAssignments;